import { request } from './client';
import type {
  LoginRequest,
  PasswordChange,
  ProfileUpdate,
  RegisterRequest,
  User,
  UsernameAvailability,
} from './types';

/** Creates an account, signs in and sends the confirmation email. */
export function register(body: RegisterRequest): Promise<User> {
  return request<User>('/auth/register', { method: 'POST', body });
}

/** Signs in with an email or username and a password. */
export function login(body: LoginRequest): Promise<User> {
  return request<User>('/auth/login', { method: 'POST', body });
}

/** Ends the session. */
export function logout(): Promise<void> {
  return request<undefined>('/auth/logout', { method: 'POST' });
}

/** Returns the signed-in user, or null when there is no session. */
export async function getMe(signal?: AbortSignal): Promise<User | null> {
  const state = await request<{ user: User | null }>('/auth/session', signal ? { signal } : {});

  return state.user;
}

/** Reports whether a username can be taken. */
export function checkUsername(
  username: string,
  signal?: AbortSignal,
): Promise<UsernameAvailability> {
  const params = new URLSearchParams({ username });

  return request<UsernameAvailability>(
    `/auth/username-available?${params.toString()}`,
    signal ? { signal } : {},
  );
}

/** Confirms an address with the token from the email. */
export function verifyEmail(token: string): Promise<User> {
  return request<User>('/auth/verify-email', { method: 'POST', body: { token } });
}

/** Sends a new confirmation email to the signed-in user. */
export function resendVerification(): Promise<void> {
  return request<undefined>('/auth/resend-verification', { method: 'POST' });
}

/** Sends a password reset link; succeeds whether or not the address is known. */
export function requestPasswordReset(email: string): Promise<void> {
  return request<undefined>('/auth/password-reset', { method: 'POST', body: { email } });
}

/** Sets a new password with the token from the reset email. */
export function resetPassword(token: string, password: string): Promise<void> {
  return request<undefined>('/auth/password-reset/confirm', {
    method: 'POST',
    body: { token, password },
  });
}

/** Changes the password of the signed-in user and ends their other sessions. */
export function changePassword(body: PasswordChange): Promise<void> {
  return request<undefined>('/auth/password', { method: 'POST', body });
}

/** Updates the profile fields that are given. */
export function updateProfile(body: ProfileUpdate): Promise<User> {
  return request<User>('/me', { method: 'PATCH', body });
}
